import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";

import { useNavigate } from "react-router-dom";
import {
  isEmailValid,
  isNameValid,
  isPasswordValid,
} from "../../helper/ValidatorsHelper";
import { useUserContext } from "../../context/useUserContext";
import {
  showErrorNotification,
  showSuccessNotification,
  showGenericError,
} from "../../helper/Notifications";
import ReactPasswordChecklist from "react-password-checklist";
import { ReactComponent as ShowIcon } from "../../assets/icons/ShowIcon.svg";
import { ReactComponent as MicrosoftLogo } from "../../assets/icons/microsoftLogo.svg";
import { ReactComponent as GoogleLogo } from "../../assets/icons/googleLogo.svg";
import CustomInput from "../Generic/CustomInput";
import PrimaryButton from "../Generic/PrimaryButton";
import LocalStorage from "../../helper/LocalStorage";

const Registercard = ({ setIsLoading }) => {
  const { createUser, getMicrosoftSSOLoginLink, getGoogleSSOLoginLink } = useUserContext();
  const navigate = useNavigate();
  const initialValues = {
    name: "",
    email: "",
    password: "",
    password_confirm: "",
  };

  const [inputValues, setInputvalues] = useState(initialValues);
  const [emailValid, setEmailValid] = useState(true);
  const [nameValid, setNameValid] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [samePasswords, setSamePasswords] = useState(true);
  const [registerEnabled, setRegisterEnabled] = useState(false);
  const [passwordRulesValid, setPasswordRulesValid] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const passwordRef = useRef(null);
  const passwordConfRef = useRef(null);

  const handleShowPassword = (ref) => {
    let element = ref.current;

    if (element.type === "password") {
      element.type = "text";
    } else {
      element.type = "password";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInputvalues({
      ...inputValues,
      [name]: value,
    });
  };

  const handleSignInBtn = () => {
    if (!isNameValid(inputValues.name)) {
      setNameValid(false);
    } else {
      setNameValid(true);
    }

    if (!isEmailValid(inputValues.email)) {
      setEmailValid(false);
      // return;
    } else {
      setEmailValid(true);
    }

    if (!isPasswordValid(inputValues.password)) {
      setPasswordValid(false);
      // return;
    } else {
      setPasswordValid(true);
    }

    if (!passwordRulesValid) {
      setShowConditions(true);
      return;
    }

    if (
      isNameValid(inputValues.name) &&
      isEmailValid(inputValues.email) &&
      passwordRulesValid
    ) {
      setIsLoading(true);
      createUser(
        inputValues.email,
        inputValues.password,
        inputValues.name,
        (response) => {
          // User created
          setIsLoading(false);
          navigate("/dashboard");
          showSuccessNotification(
            "Verify your account before signing in by following the instructions in the confirmation email. If you can't see the email check your spam folder as well."
          );
        },
        (error) => {
          if (error.response?.status === 409) {
            showErrorNotification("Email already in use");
          } else {
            showGenericError();
          }
          setIsLoading(false);
        }
      );
    }
  };

  const goToLogin = () => {
    navigate("/login");
  };

  const handleLoginMicrosoftSubmit = () => {
    LocalStorage.setSSOFlag(true);
    setIsLoading(true);

    getMicrosoftSSOLoginLink(
      (response) => {
        const microsoftLink = response.data.redirect_url;
        window.location = microsoftLink;
      },
      (error) => {
        setIsLoading(false);
        showErrorNotification("Something went wrong. Please try again.");
      }
    );
  };

  const handleLoginGoogleSubmit = () => {
    LocalStorage.setSSOFlag(true);
    setIsLoading(true);

    getGoogleSSOLoginLink(
      (response) => {
        const googleLink = response.data.redirect_url;
        window.location = googleLink;
      },
      (error) => {
        setIsLoading(false);
        showErrorNotification("Something went wrong. Please try again.");
      }
    );
  };

  useEffect(() => {
    if (
      isEmailValid(inputValues.email) &&
      isNameValid(inputValues.name) &&
      inputValues.password.length > 7 &&
      inputValues.password_confirm.length > 7
    ) {
      setRegisterEnabled(true);
    } else {
      setRegisterEnabled(false);
    }
  }, [inputValues]);

  return (
    <Card>
      <TitleContainer>
        <h1>Create account</h1>
        <p>Add your details and create a new account to use emotii for businesses.</p>
      </TitleContainer>

      <SSOButtonsWrapper>
        <SSOButton onClick={handleLoginGoogleSubmit}>
          <GoogleLogo />
          <span>Continue with Google</span>
        </SSOButton>
        <SSOButton onClick={handleLoginMicrosoftSubmit}>
          <MicrosoftLogo />
          <span>Continue with Microsoft</span>
        </SSOButton>
      </SSOButtonsWrapper>

      <Divider>
        <DividerLine />
        <DividerText>Or continue with</DividerText>
        <DividerLine />
      </Divider>

      <InputsWrapper>
        <CustomInput
          label={"Name"}
          placeholder="Olivia"
          name={"name"}
          value={inputValues.name}
          onChange={handleInputChange}
          error={!nameValid}
          errorText={"Name must be at least 3 characters"}
        />
        <CustomInput
          label={"Email"}
          placeholder="olivia@emotii.com"
          name={"email"}
          value={inputValues.email}
          onChange={handleInputChange}
          error={!emailValid}
          errorText={"Invalid email"}
        />
        <CustomInput
          type={"password"}
          label={"Password"}
          placeholder="Enter your password"
          name={"password"}
          value={inputValues.password}
          onChange={handleInputChange}
          inputRef={passwordRef}
          customButton={
            <ShowPasswordBtn onClick={() => handleShowPassword(passwordRef)}>
              <ShowIcon />
            </ShowPasswordBtn>
          }
        />

        <CustomInput
          type={"password"}
          label={"Confirm password"}
          placeholder="Confirm your password"
          name={"password_confirm"}
          value={inputValues.password_confirm}
          onChange={handleInputChange}
          inputRef={passwordConfRef}
          customButton={
            <ShowPasswordBtn
              onClick={() => handleShowPassword(passwordConfRef)}
            >
              <ShowIcon />
            </ShowPasswordBtn>
          }
        />

        <div style={showConditions ? { display: "flex" } : { display: "none" }}>
          <ReactPasswordChecklist
            iconSize={14}
            minLength={8}
            value={inputValues.password}
            valueAgain={inputValues.password_confirm}
            rules={[
              "minLength",
              "specialChar",
              "capital",
              "lowercase",
              "number",
              "match",
            ]}
            onChange={(isValid) => {
              setPasswordRulesValid(isValid);
            }}
            className="password-rules"
          />
        </div>
      </InputsWrapper>

      <SbmitWrapper>
        <PrimaryButton
          styled={{ width: "100%" }}
          text={"Create Account"}
          onClick={handleSignInBtn}
          disabled={!registerEnabled}
        />
        <SignInText>
          Already a member? <span onClick={() => goToLogin()}>Sign in</span>
        </SignInText>
      </SbmitWrapper>

      <TermsText>
        By continuing, you agree to our{" "}
        <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
      </TermsText>
    </Card>
  );
};

export default Registercard;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  align-items: center;
  max-width: 480px;
  width: 100%;
  background: ${lightThemeColors.background};
  gap: 20px;
  border-radius: 12px;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 650px) {
    width: 100%;
    padding: 30px 20px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: center;

  h1 {
    color: ${lightThemeColors.darkColor};
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    margin: 0;
  }

  p {
    color: rgba(102, 102, 102, 1);
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin: 0;
  }
`;
const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  .password-rules {
    span {
      color: ${lightThemeColors.darkColorFaded};
      font-size: 16px;
      font-weight: 400;
      line-height: 22px;
      text-align: left;
    }
  }
`;

const SbmitWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SignInText = styled.p`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: center;
  margin: 0;

  span {
    font-weight: 600;
    color: ${lightThemeColors.darkColor};
    cursor: pointer;
    text-decoration: underline;
  }
`;

const TermsText = styled.p`
  color: rgba(153, 153, 153, 1);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  margin: 0;

  a {
    color: rgba(153, 153, 153, 1);
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      color: ${lightThemeColors.darkColor};
    }
  }
`;

const SSOButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SSOButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 20px;
  background: ${lightThemeColors.background};
  border: 1px solid rgba(221, 221, 221, 1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-weight: 500;
  color: ${lightThemeColors.darkColor};

  &:hover {
    background: rgba(250, 250, 250, 1);
    border-color: rgba(200, 200, 200, 1);
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: rgba(221, 221, 221, 1);
`;

const DividerText = styled.span`
  color: rgba(102, 102, 102, 1);
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
`;

const ShowPasswordBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  jutify-content: center;
  margin-right: 10px;
  svg {
    color: ${lightThemeColors.darkColor50};
  }
`;
